import {usePlantConfig} from '@domain/shifthandover'
import {COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY} from '@hconnect/common/consts'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {useQueryParamValue} from '@hconnect/common/query-params'
import {EventsTab} from '@hconnect/common/types'
import {CircularProgress} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate, useLocation} from 'react-router'

import {EventsContainer} from '../containers/EventsContainer'
import {NotificationsContainer} from '../containers/NotificationsContainer'
import {WorkOrdersContainer} from '../containers/WorkOrdersContainer'
import {InvertedSurfaceProvider} from '../context/InvertedSurfaceContext'

const ID = 'shiftEventTab'
const KEY = COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY
const KEYS = [KEY]

type EventsPageProps = {
  plantId: string
  /**
   * Set to `true` when rendered on the legacy blue surface (Cockpit) so the
   * header controls use the on-blue contrast colours. Suite renders on the
   * light/dark page surface and keeps the default (`false`).
   */
  inverted?: boolean
}

export const EventsPage: FC<EventsPageProps> = ({plantId, inverted = false}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const {
    i18n: {language}
  } = useTranslation()
  useCancelInactiveQueries(queryClient)

  const {data: config} = usePlantConfig({plantId, language})

  const [eventTab = ''] = useQueryParamValue(ID, KEYS, {
    keepInLocalStorage: true
  })
  const activeTab = (new URLSearchParams(eventTab).get(KEY) ?? EventsTab.Events) as EventsTab

  const handleChange = (tab: EventsTab) => {
    // Use current pathname to navigate, making this component app-agnostic
    const currentPath = location.pathname
    void navigate(`${currentPath}?${KEY}=${tab}`)
  }

  if (!config) {
    return <CircularProgress />
  }

  const renderContent = () => {
    if (activeTab === EventsTab.WorkOrders) {
      return (
        <WorkOrdersContainer plantId={plantId} activeTab={activeTab} setActiveTab={handleChange} />
      )
    }
    if (activeTab === EventsTab.Notifications) {
      return (
        <NotificationsContainer
          plantId={plantId}
          activeTab={activeTab}
          setActiveTab={handleChange}
        />
      )
    }
    return <EventsContainer plantId={plantId} activeTab={activeTab} setActiveTab={handleChange} />
  }

  return <InvertedSurfaceProvider value={inverted}>{renderContent()}</InvertedSurfaceProvider>
}
